import { DomainError } from '../errors/domain-error';
import { checkPermission } from '../rbac';
import { PermissionKey, UserRole } from '../enums';

/** Publication lifecycle (Prisma + API source of truth). */
export const NewsletterPublicationStatus = {
  DRAFT: 'draft',
  ACTIVE: 'active',
  ARCHIVED: 'archived',
} as const;

export type NewsletterPublicationStatusValue =
  (typeof NewsletterPublicationStatus)[keyof typeof NewsletterPublicationStatus];

export const NEWSLETTER_PUBLICATION_STATUS_VALUES = Object.values(NewsletterPublicationStatus);

/** Consent purpose for marketing eligibility (S04 expands storage). */
export const MarketingConsentPurpose = {
  NEWSLETTER: 'newsletter',
  PRODUCT_UPDATES: 'product_updates',
  TRANSACTIONAL_ADJACENT: 'transactional_adjacent',
} as const;

export type MarketingConsentPurposeValue =
  (typeof MarketingConsentPurpose)[keyof typeof MarketingConsentPurpose];

export const MARKETING_CONSENT_PURPOSE_VALUES = Object.values(MarketingConsentPurpose);

export function isNewsletterPublicationStatus(
  value: string,
): value is NewsletterPublicationStatusValue {
  return (NEWSLETTER_PUBLICATION_STATUS_VALUES as string[]).includes(value);
}

export function assertNewsletterPublicationStatus(value: string): NewsletterPublicationStatusValue {
  if (!isNewsletterPublicationStatus(value)) {
    throw new DomainError('Invalid newsletter publication status');
  }
  return value;
}

/** Studio/API publication + email marketing management require CAN_MANAGE_MARKETING. */
export function authorizeMarketingManagement(role: UserRole): void {
  checkPermission(role, PermissionKey.CAN_MANAGE_MARKETING);
}

/** Confirm-to-send and fan-out mutations require CAN_SEND_MARKETING. */
export function authorizeMarketingSend(role: UserRole): void {
  checkPermission(role, PermissionKey.CAN_SEND_MARKETING);
}

/** Automation publish / enroll mutations require CAN_MANAGE_MARKETING_AUTOMATIONS. */
export function authorizeMarketingAutomations(role: UserRole): void {
  checkPermission(role, PermissionKey.CAN_MANAGE_MARKETING_AUTOMATIONS);
}

/**
 * Eligibility stub (S02+ fills suppression/consent/rate limits).
 * Returns false when any hard block is already known.
 */
export function isRecipientEligibleForMarketingSend(input: {
  suppressed?: boolean;
  unsubscribed?: boolean;
  hasConsent?: boolean;
  creatorMarketingSuspended?: boolean;
  platformKillSwitch?: boolean;
}): boolean {
  if (input.suppressed) return false;
  if (input.unsubscribed) return false;
  if (input.hasConsent === false) return false;
  if (input.creatorMarketingSuspended) return false;
  if (input.platformKillSwitch) return false;
  return true;
}

export function slugifyNewsletterPublicationName(name: string): string {
  const base = name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);
  return base || 'newsletter';
}

const BRAND_COLOR_RE = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

export function assertNewsletterBrandColor(value: string | null | undefined): string | null {
  if (value == null || !value.trim()) return null;
  const trimmed = value.trim();
  if (!BRAND_COLOR_RE.test(trimmed)) {
    throw new DomainError('Brand color must be a hex color (#RGB or #RRGGBB)');
  }
  return trimmed.toLowerCase();
}
